/**
 * Authentication context and provider
 * Manages auth state following FP principles with immutable state
 */
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import type { User, AuthTokens } from '../types/models';
import { api } from '../services/api/endpoints';

/**
 * Auth state
 */
type AuthState = Readonly<{
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}>;

/**
 * Initial auth state
 */
const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
};

/**
 * Auth context value
 */
type AuthContextValue = Readonly<{
  state: AuthState;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, firstName: string, lastName: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshAuth: () => Promise<void>;
}>;

/**
 * Auth context
 */
const AuthContext = createContext<AuthContextValue | undefined>(undefined);

/**
 * Token storage (in-memory + localStorage as fallback)
 * NOTE: localStorage is not secure but used for persistence
 * In production, prefer HttpOnly cookies or secure token storage
 */
let accessTokenMemory: string | null = null;

const setTokens = (tokens: AuthTokens): void => {
  accessTokenMemory = tokens.accessToken;
  localStorage.setItem('access_token', tokens.accessToken);
  localStorage.setItem('refresh_token', tokens.refreshToken);
};

const clearTokens = (): void => {
  accessTokenMemory = null;
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
};

const getAccessToken = (): string | null => {
  return accessTokenMemory || localStorage.getItem('access_token');
};

/**
 * Pure state updater functions
 */
const createLoadingState = (currentState: AuthState): AuthState => ({
  ...currentState,
  isLoading: true,
  error: null,
});

const createSuccessState = (user: User): AuthState => ({
  user,
  isAuthenticated: true,
  isLoading: false,
  error: null,
});

const createErrorState = (error: string): AuthState => ({
  user: null,
  isAuthenticated: false,
  isLoading: false,
  error,
});

const createLogoutState = (): AuthState => ({
  user: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
});

/**
 * Auth provider component
 */
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<AuthState>(initialState);
  
  /**
   * Login function
   */
  const login = useCallback(async (email: string, password: string): Promise<void> => {
    setState((prev) => createLoadingState(prev));
    
    const result = await api.auth.login({ email, password });
    
    if (result.success) {
      setTokens(result.data);
      
      // Fetch user data
      const userResult = await api.auth.me();
      
      if (userResult.success) {
        setState(createSuccessState(userResult.data));
      } else {
        setState(createErrorState(userResult.error.message));
        clearTokens();
        throw new Error(userResult.error.message);
      }
    } else {
      setState(createErrorState(result.error.message));
      throw new Error(result.error.message);
    }
  }, []);
  
  /**
   * Register function
   */
  const register = useCallback(async (
    email: string,
    password: string,
    firstName: string,
    lastName: string
  ): Promise<void> => {
    setState((prev) => createLoadingState(prev));
    
    const result = await api.auth.register({ email, password, firstName, lastName });
    
    if (result.success) {
      setTokens(result.data);
      
      // Fetch user data
      const userResult = await api.auth.me();
      
      if (userResult.success) {
        setState(createSuccessState(userResult.data));
      } else {
        setState(createErrorState(userResult.error.message));
        clearTokens();
        throw new Error(userResult.error.message);
      }
    } else {
      setState(createErrorState(result.error.message));
      throw new Error(result.error.message);
    }
  }, []);
  
  /**
   * Logout function
   */
  const logout = useCallback(async (): Promise<void> => {
    await api.auth.logout();
    clearTokens();
    setState(createLogoutState());
  }, []);
  
  /**
   * Refresh auth state
   */
  const refreshAuth = useCallback(async (): Promise<void> => {
    const token = getAccessToken();
    
    if (!token) {
      // No token means user is simply signed out, not an auth error.
      setState(createLogoutState());
      return;
    }
    
    setState((prev) => createLoadingState(prev));
    
    const result = await api.auth.me();
    
    if (result.success) {
      setState(createSuccessState(result.data));
    } else {
      setState(createErrorState(result.error.message));
      clearTokens();
    }
  }, []);
  
  /**
   * Initialize auth on mount
   */
  useEffect(() => {
    refreshAuth();
  }, [refreshAuth]);
  
  const value: AuthContextValue = {
    state,
    login,
    register,
    logout,
    refreshAuth,
  };
  
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Hook to use auth context
 */
export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  
  return context;
};

/**
 * Hook to get current user
 */
export const useCurrentUser = (): User | null => {
  const { state } = useAuth();
  return state.user;
};

/**
 * Hook to check if user is authenticated
 */
export const useIsAuthenticated = (): boolean => {
  const { state } = useAuth();
  return state.isAuthenticated;
};
